import { useState } from 'react'
import { TaskFormData, TaskStatus, TaskPriority, TaskUrgency, EhrComponentType } from '../types/task'
import { useQuery } from '@tanstack/react-query'
import { userService } from '../services/userService'
import { patientService, extractPatients } from '../services/patientService'
import { Patient } from '../types/patient'
import { formatUserName } from '../utils/name'
import './TaskForm.css'

interface TaskFormProps {
  initialData?: Partial<TaskFormData>
  onSubmit: (data: TaskFormData) => void
  onCancel: () => void
  patientId?: string
  ehrComponentType?: EhrComponentType
  ehrComponentId?: string
}

export function TaskForm({ 
  initialData, 
  onSubmit, 
  onCancel,
  patientId: propPatientId,
  ehrComponentType: propEhrComponentType,
  ehrComponentId: propEhrComponentId,
}: TaskFormProps) {
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    status: 'created',
    priority: 'medium',
    urgency: undefined,
    assignedTo: undefined,
    dueDate: '',
    patientId: propPatientId,
    ehrComponentType: propEhrComponentType,
    ehrComponentId: propEhrComponentId,
    tags: [],
    notes: '',
    ...initialData,
  })

  const [tagInput, setTagInput] = useState('')

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  const { data: patientsData } = useQuery({
    queryKey: ['patients'],
    queryFn: async () => {
      const result = await patientService.getAll()
      return extractPatients(result)
    },
    enabled: !propPatientId, // Only fetch if patient not pre-selected
  })
  const patients = patientsData || []

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) {
      alert('Title is required')
      return
    }
    onSubmit(formData)
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()],
      })
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tag) || [],
    })
  }

  return (
    <form className="task-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="task-title">Title *</label>
        <input
          id="task-title"
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          placeholder="Enter task title"
        />
      </div>

      <div className="form-group">
        <label htmlFor="task-description">Description</label>
        <textarea
          id="task-description"
          value={formData.description || ''}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          rows={3}
          placeholder="Enter task description"
        />
      </div>

      {!propPatientId && (
        <div className="form-group">
          <label htmlFor="task-patient">Patient</label>
          <select
            id="task-patient"
            value={formData.patientId || ''}
            onChange={(e) =>
              setFormData({ ...formData, patientId: e.target.value || undefined })
            }
          >
            <option value="">Select Patient</option>
            {patients.slice(0, 100).map((patient: Patient) => (
              <option key={patient.id} value={patient.id}>
                {patient.firstName} {patient.lastName}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="task-status">Status</label>
          <select
            id="task-status"
            value={formData.status || 'created'}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value as TaskStatus })
            }
          >
            <option value="created">Created</option>
            <option value="assigned">Assigned</option>
            <option value="unassigned">Unassigned</option>
            <option value="in-progress">In Progress</option>
            <option value="performed">Performed</option>
            <option value="held">Held</option>
            <option value="pended">Pended</option>
            <option value="denied">Denied</option>
            <option value="resolved">Resolved</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="task-priority">Priority</label>
          <select
            id="task-priority"
            value={formData.priority}
            onChange={(e) =>
              setFormData({
                ...formData,
                priority: e.target.value as TaskPriority,
              })
            }
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="task-urgency">Urgency</label>
          <select
            id="task-urgency"
            value={formData.urgency || ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                urgency: e.target.value ? (e.target.value as TaskUrgency) : undefined,
              })
            }
          >
            <option value="">Select Urgency</option>
            <option value="routine">Routine</option>
            <option value="urgent">Urgent</option>
            <option value="stat">Stat</option>
            <option value="asap">ASAP</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="task-assigned-to">Assign To</label>
        <select
          id="task-assigned-to"
          value={formData.assignedTo || ''}
          onChange={(e) =>
            setFormData({ ...formData, assignedTo: e.target.value || undefined })
          }
        >
          <option value="">Unassigned</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {formatUserName(user as any)} ({user.role})
            </option>
          ))}
        </select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="task-due-date">Due Date</label>
          <input
            id="task-due-date"
            type="date"
            value={formData.dueDate || ''}
            onChange={(e) =>
              setFormData({ ...formData, dueDate: e.target.value || undefined })
            }
          />
        </div>

        {!propEhrComponentType && (
          <>
            <div className="form-group">
              <label htmlFor="task-ehr-component-type">EHR Component Type</label>
              <select
                id="task-ehr-component-type"
                value={formData.ehrComponentType || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    ehrComponentType: e.target.value
                      ? (e.target.value as EhrComponentType)
                      : undefined,
                  })
                }
              >
                <option value="">None</option>
                <option value="consultation">Consultation</option>
                <option value="lab-result">Lab Result</option>
                <option value="medication">Medication</option>
                <option value="vital-sign">Vital Sign</option>
                <option value="allergy">Allergy</option>
                <option value="clinical-history">Clinical History</option>
                <option value="document">Document</option>
              </select>
            </div>

            {formData.ehrComponentType && (
              <div className="form-group">
                <label htmlFor="task-ehr-component-id">EHR Component ID</label>
                <input
                  id="task-ehr-component-id"
                  type="text"
                  value={formData.ehrComponentId || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      ehrComponentId: e.target.value || undefined,
                    })
                  }
                  placeholder="Enter component ID"
                />
              </div>
            )}
          </>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="task-notes">Notes</label>
        <textarea
          id="task-notes"
          value={formData.notes || ''}
          onChange={(e) =>
            setFormData({ ...formData, notes: e.target.value || undefined })
          }
          rows={3}
          placeholder="Additional notes or comments"
        />
      </div>

      <div className="form-group">
        <label htmlFor="task-tags">Tags</label>
        <div className="tag-input-group">
          <input
            id="task-tags"
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddTag()
              }
            }}
            placeholder="Type and press Enter to add tag"
          />
          <button
            type="button"
            className="btn-add-tag"
            onClick={handleAddTag}
          >
            Add
          </button>
        </div>
        {formData.tags && formData.tags.length > 0 && (
          <div className="tag-list">
            {formData.tags.map((tag, idx) => (
              <span key={idx} className="tag-item">
                {tag}
                <button
                  type="button"
                  className="tag-remove"
                  onClick={() => handleRemoveTag(tag)}
                  aria-label={`Remove tag ${tag}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-submit">
          {initialData ? 'Update Task' : 'Create Task'}
        </button>
        <button type="button" className="btn-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}














